import React, { useState, useMemo } from 'react';
import {
  Avatar, Button, Card, DatePicker, Descriptions, Empty, Modal, PageHeader, Space, Tag, Typography, Table, Alert,
} from 'antd';
import { CreditCardOutlined, AppleFilled, GoogleCircleFilled, LinkOutlined } from '@ant-design/icons';
import moment from 'moment';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { useUser } from '../../../models/useUser';
import { useCustomer } from '../../../models/useCustomer';
import { useCustomerVerifiedEmails } from '../../../models/useCustomerVerifiedEmails';
import { useAdminTapsByUserId } from '../../../models/useAdminTapsByUserId';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { getAppBaseUrl, getPayerBaseUrl } from '../../../utils/URLBuilder';

const PayerBaseUrl = getPayerBaseUrl();

import '../../../App.scss';
import 'antd/dist/antd.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const URLPath = getAppBaseUrl();
const TAPS_LIMIT = 100;


function walletProviderIcon(provider) {
  const norm = (provider || '').toLowerCase().replace(/[^a-z]/g, '');
  if (norm.includes('apple')) return <span><AppleFilled style={{ marginRight: 4 }} />Apple Pay</span>;
  if (norm.includes('google')) return <span><GoogleCircleFilled style={{ marginRight: 4, color: '#4285F4' }} />Google Pay</span>;
  if (norm === 'card' || norm === 'cardpresent') return <span><CreditCardOutlined style={{ marginRight: 4 }} />Card</span>;
  if (norm === 'link') return <span><LinkOutlined style={{ marginRight: 4 }} />Link</span>;
  return provider || '-';
}

function formatTimestamp(v) {
  if (!v) return '-';
  if (v.toMillis) return moment(v.toMillis()).format('YYYY-MM-DD HH:mm');
  return '-';
}

function formatCurrency(amount, currency) {
  if (amount == null) return '-';
  const ccy = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy }).format(amount / 100);
  } catch (_) {
    return `${ccy} ${(amount / 100).toFixed(2)}`;
  }
}

function makeTapColumns(onDetails) {
  return [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => formatTimestamp(v),
    },
    {
      title: '',
      key: 'recipientAvatar',
      width: 44,
      render: (_, t) => {
        const initial = (t.displayName || '?').charAt(0).toUpperCase();
        const isSplit = !!t.originatingTapId;
        const isWholeTeam = !isSplit && !!(t.teamId && !t.teamMemberId);
        const src = isWholeTeam ? (t.topLogo || null) : (t.avatar || null);
        return isWholeTeam ? (
          <Avatar shape="square" size={32} src={src} style={{ borderRadius: 8 }}>{initial}</Avatar>
        ) : (
          <Avatar size={32} src={src}>{initial}</Avatar>
        );
      },
    },
    {
      title: 'Recipient',
      key: 'displayName',
      render: (_, t) => t.displayName ? (
        <a href={`${URLPath}/admin/clients/${t.clientId}`}>{t.displayName}</a>
      ) : '-',
    },
    {
      title: 'Team',
      key: 'teamName',
      render: (_, t) => t.teamName ? (
        <a href={`${URLPath}/admin/clients/${t.clientId}/teams/${t.teamId}`}>{t.teamName}</a>
      ) : '-',
    },
    {
      title: 'Amount',
      key: 'amount',
      align: 'right',
      render: (_, t) => {
        const main = formatCurrency(t.amount, t.currency);
        let feeLabel = null;
        if (t.fee > 0) {
          feeLabel = <span style={{ color: '#52c41a' }}>+{formatCurrency(t.fee, t.currency)}</span>;
        } else if (t.strikeFee > 0) {
          feeLabel = <span style={{ color: '#ff4d4f' }}>-{formatCurrency(t.strikeFee, t.currency)}</span>;
        }
        return (
          <div style={{ textAlign: 'right', lineHeight: 1.3 }}>
            <div>{main}</div>
            {feeLabel && <div style={{ fontSize: 11 }}>{feeLabel}</div>}
          </div>
        );
      },
    },
    {
      title: 'Payment',
      key: 'payment',
      render: (_, t) => (
        <div style={{ lineHeight: 1.3 }}>
          <div>{walletProviderIcon(t.walletProvider)}</div>
          {t.processor && <div style={{ fontSize: 11, color: '#999' }}>{t.processor}</div>}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (v, t) => {
        const color = v === 'successful' ? 'green' : v === 'pending' ? 'orange' : 'default';
        return (
          <Space direction="vertical" size={2}>
            <Tag color={color}>{v || '-'}</Tag>
            {t.product && <span style={{ fontSize: 11, color: '#999' }}>{t.product}</span>}
          </Space>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      render: (_, t) => (
        <Space size={4}>
          <Button size="small" onClick={() => onDetails(t)}>Details</Button>
          {t.tagId && t.id && (
            <Button
              size="small"
              href={`${PayerBaseUrl}/tags/${t.tagId}/${t.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
          )}
        </Space>
      ),
    },
  ];
}

export default function AdminCustomerScreen(props) {
  const customerId = props && props.match && props.match.params ? props.match.params.customerId : null;
  const [loadingCustomer, customer] = useCustomer(customerId);
  const userId = customer ? customer.userId : null;
  const [loadingUser, user] = useUser(userId);
  const [dateRange, setDateRange] = useState(getCurrentWeekRange);
  const [, setPresetRefresh] = useState(0);
  const startDate = useMemo(() => (dateRange?.[0] ? dateRange[0].toDate() : null), [dateRange]);
  const endDate = useMemo(() => (dateRange?.[1] ? dateRange[1].toDate() : null), [dateRange]);
  const [loadingTaps, taps, tapsError] = useAdminTapsByUserId(userId, startDate, endDate);
  const [, verifiedEmails] = useCustomerVerifiedEmails(customerId);
  const [detailTap, setDetailTap] = useState(null);
  const tapColumns = makeTapColumns(setDetailTap);

  if (loadingCustomer) {
    return <CenteredSpinner loading={true} noLayout={false} />;
  }

  if (!customer) {
    return (
      <div style={{ height: '100%', textAlign: 'center' }}>
        <PageHeader ghost={false} title="Customer Not Found" onBack={() => window.history.back()} />
        <Empty />
      </div>
    );
  }

  const label = customer.displayName || customer.email || customer.authEmail || customer.id;

  return (
    <div style={{ height: '100%' }}>
      <PageHeader
        ghost={false}
        title={`Customer: ${label}`}
        subTitle={customer.id}
        onBack={() => window.history.back()}
      />

      <div style={{ padding: 12 }}>
        <Card>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Avatar size={64} src={customer.avatar || null}>
                {label.charAt(0).toUpperCase()}
              </Avatar>
              <Space direction="vertical" size={0}>
                <Text strong>{customer.displayName || 'No display name'}</Text>
                <Text type="secondary">{customer.email || customer.authEmail || 'No email'}</Text>
              </Space>
            </Space>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Customer ID">
                <code>{customer.id}</code>
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                {customer.userId ? (
                  <Space size={8}>
                    <a href={`${URLPath}/admin/users/${customer.userId}`}>
                      <code>{customer.userId}</code>
                    </a>
                    <a href={`${URLPath}/admin/payers/${customer.userId}`}>(payer view)</a>
                  </Space>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Login Methods">
                <Space direction="vertical" size={2}>
                  {[...new Set([customer.authProvider, ...(customer.authProviders || [])].filter(Boolean))].map((p) => {
                    const norm = p.toLowerCase();
                    let detail = null;
                    if (norm === 'phone') detail = customer.phoneNumber || user?.phoneNumber || null;
                    else if (norm === 'google') detail = user?.googleEmail || customer.email || user?.email || null;
                    else if (norm === 'apple') detail = user?.appleEmail || customer.email || user?.email || null;
                    else if (norm === 'email') detail = customer.email || user?.email || null;
                    return (
                      <Space key={p} size={6}>
                        <Tag>{p}</Tag>
                        {detail && <Text type="secondary" style={{ fontSize: 12 }}>{detail}</Text>}
                      </Space>
                    );
                  })}
                  {![...(customer.authProviders || []), customer.authProvider].filter(Boolean).length && '-'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatTimestamp(customer.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Updated">
                {formatTimestamp(customer.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        <Card title="Verified Emails" style={{ marginTop: 12 }}>
          {verifiedEmails.length === 0 ? (
            <Text type="secondary">No verified emails.</Text>
          ) : (
            <Space direction="vertical" size={4}>
              {verifiedEmails.map((ve) => (
                <Space key={ve.id} size={8}>
                  <Text>{ve.email || ve.id}</Text>
                  {ve.createdAt && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      added {formatTimestamp(ve.createdAt)}
                    </Text>
                  )}
                </Space>
              ))}
            </Space>
          )}
        </Card>

        {userId && (
          <Card title="Firebase User" style={{ marginTop: 12 }}>
            {loadingUser ? (
              <CenteredSpinner loading={true} noLayout={false} />
            ) : user ? (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Email Verified">
                  {user.emailVerified ? <Tag color="green">true</Tag> : <Tag color="orange">false</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Platform">{user.platform || 'web'}</Descriptions.Item>
                <Descriptions.Item label="Roles">
                  {Array.isArray(user.roles) && user.roles.length > 0
                    ? user.roles.map((r) => <Tag key={r}>{r}</Tag>)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="User doc">
                  <a href={`${URLPath}/admin/users/${userId}`}>View in Users</a>
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">No user document found for uid: {userId}</Text>
            )}
          </Card>
        )}

        <Card
          title={`Taps (max ${TAPS_LIMIT})`}
          style={{ marginTop: 12 }}
          extra={
            <Space>
              {taps != null && <Text type="secondary">{taps.length} taps</Text>}
              <RangePicker
                value={dateRange}
                onChange={(range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange())}
                onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
                ranges={getAdminDatePresets()}
                allowClear={false}
              />
            </Space>
          }
        >
          {tapsError && (
            <Alert
              type="warning"
              message="Could not load taps"
              description={`This query requires a Firestore collection group index on striker_userId + createdAt. ${tapsError}`}
              style={{ marginBottom: 12 }}
            />
          )}
          {loadingTaps ? (
            <CenteredSpinner loading={true} noLayout={false} />
          ) : (taps && taps.length > 0) ? (
            <Table
              rowKey="id"
              dataSource={taps}
              columns={tapColumns}
              scroll={{ x: 900 }}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              size="small"
            />
          ) : (
            <Empty description="No taps found for this user" />
          )}
        </Card>
      </div>

      <Modal
        title={`Tap: ${detailTap ? detailTap.id : ''}`}
        visible={!!detailTap}
        onCancel={() => setDetailTap(null)}
        footer={<Button onClick={() => setDetailTap(null)}>Close</Button>}
        width={640}
      >
        {detailTap && (
          <Descriptions bordered column={1} size="small">
            {Object.entries(detailTap)
              .filter(([k]) => k !== 'id')
              .map(([k, v]) => {
                let display = v;
                if (v && typeof v === 'object' && v.toMillis) {
                  display = moment(v.toMillis()).format('YYYY-MM-DD HH:mm:ss');
                } else if (v !== null && typeof v === 'object') {
                  display = JSON.stringify(v);
                } else if (v === null || v === undefined) {
                  display = '-';
                } else {
                  display = `${v}`;
                }
                return (
                  <Descriptions.Item key={k} label={<code>{k}</code>}>
                    {display}
                  </Descriptions.Item>
                );
              })}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
